import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'motion/react';
import {
  Edit3,
  ArrowLeft,
  ArrowRight,
  Trash2,
  Plus,
  Wand2,
  Save,
  Loader2,
  Eye,
  Copy,
  Move,
  RefreshCw } from
'lucide-react';
import { geminiService } from '@/services/geminiService';

interface Slide {
  id: string;
  title: string;
  content: string;
  type: string;
  notes: string;
  imageKeywords: string;
  interactiveElement?: string;
}

interface ContentEditorProps {
  presentationData: any;
  updatePresentationData: (updates: any) => void;
  onNext: () => void;
  onPrevious: () => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

const ContentEditor: React.FC<ContentEditorProps> = ({
  presentationData,
  updatePresentationData,
  onNext,
  onPrevious,
  isLoading,
  setIsLoading
}) => {
  // Initialize slides from presentationData.content
  const [slides, setSlides] = useState<Slide[]>([]);
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const [isRegenerating, setIsRegenerating] = useState<number | null>(null);
  const { toast } = useToast();

  // Debug and initialize slides from presentation data
  useEffect(() => {
    console.log('📊 ContentEditor - presentationData:', presentationData);

    if (presentationData?.content && Array.isArray(presentationData.content)) {
      console.log('✅ Found content array with', presentationData.content.length, 'slides');
      setSlides(presentationData.content);
    } else if (presentationData?.slides && Array.isArray(presentationData.slides)) {
      console.log('✅ Found slides array with', presentationData.slides.length, 'slides');
      setSlides(presentationData.slides);
    } else {
      console.log('❌ No valid slides content found in presentationData');
      console.log('Available keys:', Object.keys(presentationData || {}));
    }
  }, [presentationData]);

  const selectedSlide = slides[selectedSlideIndex];

  const updateSlide = (index: number, updates: Partial<Slide>) => {
    const updatedSlides = slides.map((slide, i) =>
    i === index ? { ...slide, ...updates } : slide
    );
    setSlides(updatedSlides);
    updatePresentationData({
      ...presentationData,
      content: updatedSlides
    });
  };

  const deleteSlide = (index: number) => {
    if (slides.length <= 1) {
      toast({
        title: "Cannot Delete",
        description: "You must have at least one slide in your presentation.",
        variant: "destructive"
      });
      return;
    }

    const updatedSlides = slides.filter((_, i) => i !== index);
    setSlides(updatedSlides);
    updatePresentationData({
      ...presentationData,
      content: updatedSlides
    });

    // Adjust selected slide index if necessary
    if (selectedSlideIndex >= updatedSlides.length) {
      setSelectedSlideIndex(updatedSlides.length - 1);
    }

    toast({
      title: "Slide Deleted",
      description: "The slide has been removed from your presentation."
    });
  };

  const addSlide = () => {
    const newSlide: Slide = {
      id: `slide-${Date.now()}`,
      title: 'New Slide',
      content: 'Add your content here...',
      type: 'content',
      notes: 'Speaker notes for this slide...',
      imageKeywords: 'education,learning',
      interactiveElement: ''
    };

    const updatedSlides = [...slides, newSlide];
    setSlides(updatedSlides);
    updatePresentationData({
      ...presentationData,
      content: updatedSlides
    });
    setSelectedSlideIndex(updatedSlides.length - 1);

    toast({
      title: "Slide Added",
      description: "A new slide has been added to your presentation."
    });
  };

  const duplicateSlide = (index: number) => {
    const slideToClone = slides[index];
    const duplicatedSlide: Slide = {
      ...slideToClone,
      id: `slide-${Date.now()}`,
      title: `${slideToClone.title} (Copy)`
    };

    const updatedSlides = [
    ...slides.slice(0, index + 1),
    duplicatedSlide,
    ...slides.slice(index + 1)];


    setSlides(updatedSlides);
    updatePresentationData({
      ...presentationData,
      content: updatedSlides
    });
    setSelectedSlideIndex(index + 1);

    toast({
      title: "Slide Duplicated",
      description: "The slide has been duplicated."
    });
  };

  const moveSlide = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= slides.length) return;

    const updatedSlides = [...slides];
    const [movedSlide] = updatedSlides.splice(fromIndex, 1);
    updatedSlides.splice(toIndex, 0, movedSlide);

    setSlides(updatedSlides);
    updatePresentationData({
      ...presentationData,
      content: updatedSlides
    });
    setSelectedSlideIndex(toIndex);

    toast({
      title: "Slide Moved",
      description: `Slide moved to position ${toIndex + 1}.`
    });
  };

  const regenerateSlide = async (index: number) => {
    setIsRegenerating(index);

    try {
      const slide = slides[index];
      const prompt = `Regenerate the content for this slide with improved quality and engagement:

Current Slide:
Title: ${slide.title}
Content: ${slide.content}
Type: ${slide.type}

Context:
- Presentation Topic: ${presentationData.topic}
- Subject: ${presentationData.subject}
- Grade Level: ${presentationData.gradeLevel}
- Target Audience: ${presentationData.targetAudience}

Please provide an improved version that is more engaging, educational, and appropriate for the audience. Return ONLY a JSON object with this structure:
{
  "title": "Improved slide title",
  "content": "Enhanced slide content with key points",
  "notes": "Detailed speaker notes for the teacher",
  "imageKeywords": "relevant,keywords,for,images",
  "interactiveElement": "Optional discussion questions or activities"
}`;

      const response = await geminiService(prompt);

      try {
        const regeneratedData = JSON.parse(response);

        updateSlide(index, {
          title: regeneratedData.title || slide.title,
          content: regeneratedData.content || slide.content,
          notes: regeneratedData.notes || slide.notes,
          imageKeywords: regeneratedData.imageKeywords || slide.imageKeywords,
          interactiveElement: regeneratedData.interactiveElement || slide.interactiveElement
        });

        toast({
          title: "Slide Regenerated",
          description: "The slide content has been improved with AI."
        });
      } catch (parseError) {
        // Fallback: use the response as improved content
        updateSlide(index, {
          content: response.substring(0, 500) + (response.length > 500 ? '...' : '')
        });

        toast({
          title: "Slide Updated",
          description: "The slide has been updated with AI suggestions."
        });
      }
    } catch (error) {
      console.error('Error regenerating slide:', error);
      toast({
        title: "Regeneration Failed",
        description: "Failed to regenerate slide content. Please try editing manually.",
        variant: "destructive"
      });
    } finally {
      setIsRegenerating(null);
    }
  };

  const handleContinue = () => {
    if (slides.length === 0) {
      toast({
        title: "No Content",
        description: "Please add at least one slide before continuing.",
        variant: "destructive"
      });
      return;
    }

    updatePresentationData({
      ...presentationData,
      content: slides
    });
    onNext();
  };

  // Show debug info and fallback UI if no slides
  if (!slides || slides.length === 0) {
    return (
      <div className="text-center py-12" data-id="w8jbjfxpv" data-path="src/components/ai/ContentEditor.tsx">
        <div className="max-w-md mx-auto" data-id="5tq7goskj" data-path="src/components/ai/ContentEditor.tsx">
          <Edit3 className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="g6b7xikdo" data-path="src/components/ai/ContentEditor.tsx" />
          <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="r4ivqfoer" data-path="src/components/ai/ContentEditor.tsx">No Content to Edit</h3>
          <p className="text-gray-600 mb-6" data-id="9ukgvi3qa" data-path="src/components/ai/ContentEditor.tsx">
            Go back to the previous step to generate content first.
          </p>
          
          {/* Debug info */}
          <div className="bg-gray-100 p-4 rounded-lg mb-4 text-left" data-id="0sdw711yl" data-path="src/components/ai/ContentEditor.tsx">
            <h4 className="font-medium text-sm mb-2" data-id="ofwb5nyja" data-path="src/components/ai/ContentEditor.tsx">Debug Info:</h4>
            <pre className="text-xs text-gray-600 overflow-auto" data-id="ipyizaxbu" data-path="src/components/ai/ContentEditor.tsx">
              {JSON.stringify(presentationData, null, 2).substring(0, 500)}...
            </pre>
          </div>
          
          <div className="flex gap-2 justify-center" data-id="ujab8w9ye" data-path="src/components/ai/ContentEditor.tsx">
            <Button onClick={onPrevious} variant="outline" data-id="uua2u06oo" data-path="src/components/ai/ContentEditor.tsx">
              <ArrowLeft className="h-4 w-4 mr-2" data-id="wg13ismp1" data-path="src/components/ai/ContentEditor.tsx" />
              Back to Content Planning
            </Button>
            <Button onClick={addSlide} variant="default" data-id="ncj9yfef3" data-path="src/components/ai/ContentEditor.tsx">
              <Plus className="h-4 w-4 mr-2" data-id="o5ar9g1a0" data-path="src/components/ai/ContentEditor.tsx" />
              Add First Slide
            </Button>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="space-y-6" data-id="e6ndb99li" data-path="src/components/ai/ContentEditor.tsx">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="6k3ghg6c2" data-path="src/components/ai/ContentEditor.tsx">
        <div data-id="0yt4jhg9a" data-path="src/components/ai/ContentEditor.tsx">
          <h3 className="text-lg font-medium" data-id="q2eyf6rhn" data-path="src/components/ai/ContentEditor.tsx">Edit Presentation Content</h3>
          <p className="text-sm text-gray-600" data-id="oguze5oun" data-path="src/components/ai/ContentEditor.tsx">
            Review and customize your {slides.length} slides
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="9mmvy7usp" data-path="src/components/ai/ContentEditor.tsx">
          <Badge variant="outline" data-id="ogc6o39a7" data-path="src/components/ai/ContentEditor.tsx">{slides.length} slides</Badge>
          <Button onClick={addSlide} size="sm" variant="outline" data-id="ek8zmdozk" data-path="src/components/ai/ContentEditor.tsx">
            <Plus className="h-4 w-4 mr-2" data-id="a8gfvvue4" data-path="src/components/ai/ContentEditor.tsx" />
            Add Slide
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="kwrjhgeby" data-path="src/components/ai/ContentEditor.tsx">
        {/* Slides navigation */}
        <div className="space-y-3" data-id="rh9hgikq3" data-path="src/components/ai/ContentEditor.tsx">
          <h4 className="font-medium text-sm text-gray-700" data-id="b6yetwzt5" data-path="src/components/ai/ContentEditor.tsx">Slides</h4>
          <div className="space-y-2 max-h-96 overflow-y-auto" data-id="aqzptsucs" data-path="src/components/ai/ContentEditor.tsx">
            {slides.map((slide, index) =>
            <Card
              key={slide.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
              selectedSlideIndex === index ?
              'ring-2 ring-blue-500 bg-blue-50' :
              'hover:bg-gray-50'}`
              }
              onClick={() => setSelectedSlideIndex(index)} data-id="dhpp94wyw" data-path="src/components/ai/ContentEditor.tsx">
                <CardContent className="p-3" data-id="qno13xlgf" data-path="src/components/ai/ContentEditor.tsx">
                  <div className="flex items-center justify-between mb-2" data-id="ouz7a1pj7" data-path="src/components/ai/ContentEditor.tsx">
                    <Badge variant="outline" className="text-xs" data-id="2lhqhgv4d" data-path="src/components/ai/ContentEditor.tsx">
                      {index + 1}
                    </Badge>
                    <div className="flex items-center gap-1" data-id="g8mpqhasr" data-path="src/components/ai/ContentEditor.tsx">
                      <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        duplicateSlide(index);
                      }} data-id="f57sd73j5" data-path="src/components/ai/ContentEditor.tsx">
                        <Copy className="h-3 w-3" data-id="h1cvu7ifz" data-path="src/components/ai/ContentEditor.tsx" />
                      </Button>
                      <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-red-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteSlide(index);
                      }} data-id="wyv0v3s6c" data-path="src/components/ai/ContentEditor.tsx">
                        <Trash2 className="h-3 w-3" data-id="9vwgebmfu" data-path="src/components/ai/ContentEditor.tsx" />
                      </Button>
                    </div>
                  </div>
                  <h5 className="text-sm font-medium line-clamp-2 mb-1" data-id="o5sv0pzk4" data-path="src/components/ai/ContentEditor.tsx">
                    {slide.title}
                  </h5>
                  <p className="text-xs text-gray-600 line-clamp-2" data-id="2ph5vt83q" data-path="src/components/ai/ContentEditor.tsx">
                    {slide.content}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Slide editor */}
        <div className="lg:col-span-3" data-id="smlhxy52m" data-path="src/components/ai/ContentEditor.tsx">
          <AnimatePresence mode="wait" data-id="non7f842k" data-path="src/components/ai/ContentEditor.tsx">
            <motion.div
              key={selectedSlideIndex}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }} data-id="ic3e96c4d" data-path="src/components/ai/ContentEditor.tsx">
              <Card data-id="165yglvef" data-path="src/components/ai/ContentEditor.tsx">
                <CardHeader data-id="n53o06lei" data-path="src/components/ai/ContentEditor.tsx">
                  <div className="flex items-center justify-between" data-id="zvwd2k0ts" data-path="src/components/ai/ContentEditor.tsx">
                    <CardTitle className="flex items-center gap-2" data-id="iqokz6hae" data-path="src/components/ai/ContentEditor.tsx">
                      <Edit3 className="h-5 w-5 text-blue-600" data-id="vfgq0thv8" data-path="src/components/ai/ContentEditor.tsx" />
                      Slide {selectedSlideIndex + 1}
                      <Badge variant="secondary" data-id="8zzsd8rj3" data-path="src/components/ai/ContentEditor.tsx">{selectedSlide.type}</Badge>
                    </CardTitle>
                    <div className="flex items-center gap-2" data-id="f7vzbrhyh" data-path="src/components/ai/ContentEditor.tsx">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => moveSlide(selectedSlideIndex, selectedSlideIndex - 1)}
                        disabled={selectedSlideIndex === 0} data-id="fnwk4eq92" data-path="src/components/ai/ContentEditor.tsx">
                        ←
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => moveSlide(selectedSlideIndex, selectedSlideIndex + 1)}
                        disabled={selectedSlideIndex === slides.length - 1} data-id="vdpnaz9o4" data-path="src/components/ai/ContentEditor.tsx">
                        →
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => regenerateSlide(selectedSlideIndex)}
                        disabled={isRegenerating === selectedSlideIndex} data-id="7cc2w1sng" data-path="src/components/ai/ContentEditor.tsx">
                        {isRegenerating === selectedSlideIndex ?
                        <Loader2 className="h-4 w-4 animate-spin" data-id="m4xlzr1jc" data-path="src/components/ai/ContentEditor.tsx" /> :
                        <RefreshCw className="h-4 w-4" data-id="0u4o6co1v" data-path="src/components/ai/ContentEditor.tsx" />
                        }
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4" data-id="2vglcb2vj" data-path="src/components/ai/ContentEditor.tsx">
                  <div data-id="mhrlyxwic" data-path="src/components/ai/ContentEditor.tsx">
                    <Label htmlFor="slide-title" data-id="c021iumbh" data-path="src/components/ai/ContentEditor.tsx">Title</Label>
                    <Input
                      id="slide-title"
                      value={selectedSlide.title}
                      onChange={(e) => updateSlide(selectedSlideIndex, { title: e.target.value })}
                      className="mt-1" data-id="n5nyofkw2" data-path="src/components/ai/ContentEditor.tsx" />
                  </div>

                  <div data-id="ac3zo35xh" data-path="src/components/ai/ContentEditor.tsx">
                    <Label htmlFor="slide-content" data-id="925xas08z" data-path="src/components/ai/ContentEditor.tsx">Content</Label>
                    <Textarea
                      id="slide-content"
                      value={selectedSlide.content}
                      onChange={(e) => updateSlide(selectedSlideIndex, { content: e.target.value })}
                      rows={6}
                      className="mt-1" data-id="yqd8p3uys" data-path="src/components/ai/ContentEditor.tsx" />
                  </div>

                  <div data-id="ca70qu1yw" data-path="src/components/ai/ContentEditor.tsx">
                    <Label htmlFor="slide-notes" data-id="y8n8uvxvt" data-path="src/components/ai/ContentEditor.tsx">Speaker Notes</Label>
                    <Textarea
                      id="slide-notes"
                      value={selectedSlide.notes}
                      onChange={(e) => updateSlide(selectedSlideIndex, { notes: e.target.value })}
                      rows={3}
                      className="mt-1"
                      placeholder="Add notes for the presenter..." data-id="vf4corguf" data-path="src/components/ai/ContentEditor.tsx" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="ulybcjc77" data-path="src/components/ai/ContentEditor.tsx">
                    <div data-id="94xu2q0em" data-path="src/components/ai/ContentEditor.tsx">
                      <Label htmlFor="slide-keywords" data-id="u6xeutizw" data-path="src/components/ai/ContentEditor.tsx">Image Keywords</Label>
                      <Input
                        id="slide-keywords"
                        value={selectedSlide.imageKeywords}
                        onChange={(e) => updateSlide(selectedSlideIndex, { imageKeywords: e.target.value })}
                        className="mt-1"
                        placeholder="education, learning, science" data-id="hftqakwa1" data-path="src/components/ai/ContentEditor.tsx" />
                    </div>
                    <div data-id="p1tk1bex0" data-path="src/components/ai/ContentEditor.tsx">
                      <Label htmlFor="slide-type" data-id="uufpkmusd" data-path="src/components/ai/ContentEditor.tsx">Slide Type</Label>
                      <select
                        id="slide-type"
                        value={selectedSlide.type}
                        onChange={(e) => updateSlide(selectedSlideIndex, { type: e.target.value })}
                        className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="brn4anczp" data-path="src/components/ai/ContentEditor.tsx">
                        <option value="title" data-id="mvxascn5d" data-path="src/components/ai/ContentEditor.tsx">Title</option>
                        <option value="content" data-id="o294wcc4r" data-path="src/components/ai/ContentEditor.tsx">Content</option>
                        <option value="list" data-id="ixbe0esqp" data-path="src/components/ai/ContentEditor.tsx">List</option>
                        <option value="image" data-id="813n7btdt" data-path="src/components/ai/ContentEditor.tsx">Image</option>
                        <option value="quote" data-id="t8oyu8kae" data-path="src/components/ai/ContentEditor.tsx">Quote</option>
                        <option value="comparison" data-id="henkibzq3" data-path="src/components/ai/ContentEditor.tsx">Comparison</option>
                        <option value="conclusion" data-id="ter4ae8j3" data-path="src/components/ai/ContentEditor.tsx">Conclusion</option>
                      </select>
                    </div>
                  </div>

                  {selectedSlide.interactiveElement !== undefined &&
                  <div data-id="ywh6w65a3" data-path="src/components/ai/ContentEditor.tsx">
                      <Label htmlFor="interactive-element" data-id="55uc9g0e2" data-path="src/components/ai/ContentEditor.tsx">Interactive Element</Label>
                      <Textarea
                      id="interactive-element"
                      value={selectedSlide.interactiveElement}
                      onChange={(e) => updateSlide(selectedSlideIndex, { interactiveElement: e.target.value })}
                      rows={2}
                      className="mt-1"
                      placeholder="Discussion questions, activities, etc." data-id="jphec1yyj" data-path="src/components/ai/ContentEditor.tsx" />
                    </div>
                  }
                </CardContent>
              </Card>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-between pt-6 border-t" data-id="xqxud98l0" data-path="src/components/ai/ContentEditor.tsx">
        <Button
          onClick={onPrevious}
          variant="outline"
          className="flex items-center gap-2" data-id="0i6an400v" data-path="src/components/ai/ContentEditor.tsx">
          <ArrowLeft className="h-4 w-4" data-id="0joijzs8e" data-path="src/components/ai/ContentEditor.tsx" />
          Back to Planning
        </Button>

        <div className="flex items-center gap-3" data-id="xc5avhrnl" data-path="src/components/ai/ContentEditor.tsx">
          <span className="text-sm text-gray-600" data-id="twx9tdwga" data-path="src/components/ai/ContentEditor.tsx">
            {slides.length} slides ready
          </span>
          <Button
            onClick={handleContinue}
            className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700" data-id="9d9ia931e" data-path="src/components/ai/ContentEditor.tsx">
            Continue to Review
            <ArrowRight className="h-4 w-4" data-id="w5y1owh73" data-path="src/components/ai/ContentEditor.tsx" />
          </Button>
        </div>
      </div>
    </div>);

};

export default ContentEditor;